<template>
    <DefaultField
        :field="currentField"
        :errors="errors"
        :show-help-text="showHelpText"
        :full-width-content="fullWidthContent"
    >
        <template #field>
            <div class="persian-time-field w-full">
                <date-picker
                    :class="errorClasses"
                    :clearable="true"
                    :color="color"
                    compact-time
                    :disabled="isPickerDisabled"
                    :format="format"
                    :id="currentField.uniqueKey"
                    :initial-value="fieldValue"
                    :input-attrs="{ style: 'direction: ltr; text-align: end;' }"
                    inputClass="w-full form-control form-input form-input-bordered"
                    :locale="locale"
                    :min="effectiveMin"
                    :placeholder="placeholder"
                    type="time"
                    v-model="value"
                >
                </date-picker>
            </div>
        </template>
    </DefaultField>
</template>

<script>
import Vue3PersianDatetimePicker from 'vue3-persian-datetime-picker'
import { DependentFormField, HandlesValidationErrors } from '@/mixins'
import jMoment from 'moment-jalaali'

export default {
    components: {
        DatePicker: Vue3PersianDatetimePicker,
    },

    mixins: [DependentFormField, HandlesValidationErrors],

    inject: {
        viaParent: { default: null },
        rowIndex: { from: 'index', default: null },
    },

    data: () => ({
        rowStartTime: '',
        startTimeListener: null,
    }),

    computed: {
        color() {
            return this.currentField.color || 'rgb(30, 136, 229)'
        },

        fieldValue() {
            return this.normalizedValue(this.currentField.value)
        },

        format() {
            return this.currentField.format || 'HH:mm'
        },

        locale() {
            return this.currentField.locale || 'fa,en'
        },

        isEndTimeField() {
            return this.fieldAttribute === 'end_time'
        },

        isStartTimeField() {
            return this.fieldAttribute === 'start_time'
        },

        rowScopePrefix() {
            if (this.viaParent == null || this.rowIndex == null) {
                return null
            }

            return `${this.viaParent}[${this.rowIndex}]`
        },

        startTimeChangeEventName() {
            if (this.rowScopePrefix) {
                return `${this.rowScopePrefix}-start_time-change`
            }

            return 'start_time-change'
        },

        placeholder() {
            if (this.isEndTimeField && ! this.rowStartTime) {
                return this.__('Select start time first')
            }

            return this.currentField.placeholder || '00:00'
        },

        min() {
            return this.currentField.min || null
        },

        effectiveMin() {
            if (this.isEndTimeField && this.rowStartTime) {
                return this.addMinutesToTime(this.rowStartTime, 1)
            }

            return this.min
        },

        isPickerDisabled() {
            if (this.isEndTimeField) {
                return ! this.rowStartTime
            }

            return this.currentlyIsReadonly
        },
    },

    watch: {
        value(newValue) {
            if (! this.isStartTimeField) {
                return
            }

            Nova.$emit(this.startTimeChangeEventName, newValue ?? '')
        },
    },

    mounted() {
        if (this.isEndTimeField) {
            this.startTimeListener = value => {
                this.onRowStartTimeChange(value)
            }

            Nova.$on(this.startTimeChangeEventName, this.startTimeListener)
        }

        if (this.isStartTimeField) {
            this.$nextTick(() => {
                Nova.$emit(this.startTimeChangeEventName, this.value ?? '')
            })
        }
    },

    beforeUnmount() {
        if (this.startTimeListener) {
            Nova.$off(this.startTimeChangeEventName, this.startTimeListener)
        }
    },

    methods: {
        fill(formData) {
            this.fillIfVisible(formData, this.field.attribute, this.normalizedValue(this.value) || '')
        },

        normalizedValue(value) {
            if (! value) {
                return ''
            }

            const parsed = jMoment(value, ['HH:mm', 'HH:mm:ss', this.format], true)

            return parsed.isValid() ? parsed.format('HH:mm') : String(value).slice(0, 5)
        },

        setInitialValue() {
            this.value = this.fieldValue
        },

        onRowStartTimeChange(value) {
            const startTime = this.normalizedValue(value)
            this.rowStartTime = startTime

            if (! startTime) {
                this.value = ''

                return
            }

            if (this.value && this.value <= startTime) {
                this.value = ''
            }
        },

        addMinutesToTime(time, minutes) {
            const parsed = jMoment(time, 'HH:mm', true)

            if (! parsed.isValid()) {
                return time
            }

            return parsed.add(minutes, 'minutes').format('HH:mm')
        },
    },
}
</script>

<style>
.vpd-next svg,
.vpd-prev svg {
    display: inline;
}
</style>
